import React from 'react';
import { Helmet } from 'react-helmet';
import { FileText, AlertTriangle, Shield, CreditCard, Scale, Users, Mail } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      <Helmet>
        <title>Terms of Service | ActionFraudOne</title>
        <meta name="description" content="ActionFraudOne Terms of Service. Read our terms and conditions for fraud investigation and scam analysis services." />
      </Helmet>

      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 border border-gold/30 rounded-full mb-6">
              <FileText className="w-4 h-4 text-gold" />
              <span className="text-gold text-sm font-medium">Legal Terms</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Terms of Service</h1>
            <p className="text-gray-400">Last updated: March 2026</p>
          </div>

          {/* Content */}
          <div className="space-y-8">
            {/* Introduction */}
            <Card className="bg-[#111] border-gold/20">
              <CardContent className="p-6 md:p-8">
                <h2 className="text-xl font-bold text-white mb-4">1. Introduction</h2>
                <div className="text-gray-400 space-y-4">
                  <p>
                    These Terms of Service ("Terms") govern your use of the services provided by ActionFraudOne 
                    Consultancy ("we", "our", "us"). By using our services, you agree to be bound by these Terms.
                  </p>
                  <p>
                    Please read these Terms carefully before purchasing any services. If you do not agree to these 
                    Terms, you should not use our services.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Nature of Services */}
            <Card className="bg-[#111] border-gold/20">
              <CardContent className="p-6 md:p-8">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-gold" />
                  2. Nature of Our Services
                </h2>
                <div className="text-gray-400 space-y-4">
                  <p>ActionFraudOne provides:</p>
                  <ul className="space-y-2 mt-4">
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span>Fraud analysis and scam identification services</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span>Investigative reporting and evidence documentation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span>Scam risk assessments</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span>Professional investigation dossiers</span>
                    </li>
                  </ul>
                  
                  <div className="p-4 bg-yellow-500/5 border border-yellow-500/20 rounded-lg mt-6">
                    <p className="text-yellow-500 font-semibold mb-2">Important:</p>
                    <p className="text-sm">
                      All services provided are investigative and analytical in nature. We analyse information, 
                      identify patterns, and document findings. Our services are designed to assist with reporting 
                      to relevant authorities, not to replace them.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* What We Are Not */}
            <Card className="bg-[#111] border-gold/20">
              <CardContent className="p-6 md:p-8">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Scale className="w-5 h-5 text-gold" />
                  3. What We Are Not
                </h2>
                <div className="text-gray-400 space-y-4">
                  <p>ActionFraudOne is <strong className="text-white">NOT</strong>:</p>
                  <ul className="space-y-2 mt-4">
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">✗</span>
                      <span>A law firm or legal practice</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">✗</span>
                      <span>A law enforcement agency</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">✗</span>
                      <span>A financial institution or bank</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">✗</span>
                      <span>A fund recovery company</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">✗</span>
                      <span>A licensed private investigator (in the traditional law enforcement sense)</span>
                    </li>
                  </ul>
                  <p className="mt-4 text-sm">
                    We do not provide legal advice, represent clients in legal proceedings, or have powers of arrest 
                    or seizure. Our reports are analytical documents that may assist clients in their dealings with 
                    appropriate authorities.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* No Guarantees */}
            <Card className="bg-red-500/5 border-red-500/20">
              <CardContent className="p-6 md:p-8">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  4. No Financial Recovery Guarantee
                </h2>
                <div className="text-gray-400 space-y-4">
                  <p className="text-red-400 font-semibold">
                    ActionFraudOne does not guarantee financial recovery or any specific legal outcome.
                  </p>
                  <p>
                    Our services provide investigation, analysis, and documentation only. Recovery of lost funds 
                    depends on many factors outside our control, including:
                  </p>
                  <ul className="space-y-2 mt-4">
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span>The nature and complexity of the fraud</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span>The jurisdiction and applicable laws</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span>Actions taken by law enforcement</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span>Decisions made by financial institutions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span>The availability of recoverable assets</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Client Responsibilities */}
            <Card className="bg-[#111] border-gold/20">
              <CardContent className="p-6 md:p-8">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-gold" />
                  5. Client Responsibilities
                </h2>
                <div className="text-gray-400 space-y-4">
                  <p>By using our services, you agree to:</p>
                  <ul className="space-y-2 mt-4">
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">✓</span>
                      <span>Provide accurate and truthful information</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">✓</span>
                      <span>Only submit information you have the lawful right to share</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">✓</span>
                      <span>Not upload content that violates privacy laws or third-party rights</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">✓</span>
                      <span>Not use our services for unlawful purposes</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">✓</span>
                      <span>Respond promptly to requests for additional information</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Payments */}
            <Card className="bg-[#111] border-gold/20">
              <CardContent className="p-6 md:p-8">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-gold" />
                  6. Payments and Refunds
                </h2>
                <div className="text-gray-400 space-y-4">
                  <p>
                    All payments are processed securely through Stripe. Prices are displayed in GBP and include 
                    applicable taxes.
                  </p>
                  <div className="space-y-4 mt-4">
                    <div className="p-4 bg-black/50 rounded-lg border border-gold/10">
                      <h3 className="text-white font-semibold mb-2">Scam Check Services</h3>
                      <p className="text-sm">
                        Payment is required before submission. Due to the nature of investigative services, 
                        refunds are generally not provided once analysis has commenced. Contact us if you 
                        believe there are exceptional circumstances.
                      </p>
                    </div>
                    <div className="p-4 bg-black/50 rounded-lg border border-gold/10">
                      <h3 className="text-white font-semibold mb-2">Fraud Investigation Dossier</h3>
                      <p className="text-sm">
                        Payment terms are agreed upon engagement. A deposit may be required. Final payment 
                        is due upon delivery of the investigation report.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Limitation of Liability */}
            <Card className="bg-[#111] border-gold/20">
              <CardContent className="p-6 md:p-8">
                <h2 className="text-xl font-bold text-white mb-4">7. Limitation of Liability</h2>
                <div className="text-gray-400 space-y-4">
                  <p>
                    To the maximum extent permitted by law, ActionFraudOne shall not be liable for:
                  </p>
                  <ul className="space-y-2 mt-4">
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span>Any indirect, incidental, or consequential damages</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span>Loss of profits or financial losses</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span>Outcomes of any legal proceedings or disputes</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span>Actions or decisions made by third parties based on our reports</span>
                    </li>
                  </ul>
                  <p className="mt-4 text-sm">
                    Our total liability shall not exceed the amount paid for the specific service in question.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Governing Law */}
            <Card className="bg-[#111] border-gold/20">
              <CardContent className="p-6 md:p-8">
                <h2 className="text-xl font-bold text-white mb-4">8. Governing Law</h2>
                <div className="text-gray-400 space-y-4">
                  <p>
                    These Terms shall be governed by and construed in accordance with the laws of England and Wales. 
                    Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the 
                    courts of England and Wales.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card className="bg-[#111] border-gold/20">
              <CardContent className="p-6 md:p-8">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-gold" />
                  9. Contact Us
                </h2>
                <div className="text-gray-400 space-y-4">
                  <p>
                    If you have any questions about these Terms of Service, please contact us:
                  </p>
                  <div className="p-4 bg-black/50 rounded-lg border border-gold/10 mt-4">
                    <p className="text-white font-semibold">ActionFraudOne</p>
                    <p>167-169 Great Portland Street, 5th Floor</p>
                    <p>London, W1W 5PF</p>
                    <p className="mt-2">
                      Email: <a href="mailto:info@actionfraudone.com" className="text-gold hover:underline">info@actionfraudone.com</a>
                    </p>
                    <p>Phone: +44 7386 668966</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Acceptance */}
            <div className="p-6 bg-gold/5 border border-gold/30 rounded-lg text-center">
              <p className="text-gray-300">
                By using our services, you acknowledge that you have read, understood, and agree to be bound 
                by these Terms of Service and our Privacy Policy.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
